import jsPDF from "jspdf";
import {
  AlertCircle,
  Calendar,
  Download,
  Eye,
  Lock,
  Mail,
  Phone,
  Printer,
  Settings,
  Share2,
  Shield,
  Users,
} from "lucide-react";
import { useState } from "react";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";

export default function PrivacyPolicy() {
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const handlePrintPolicy = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const maxWidth = pageWidth - 2 * margin;
      let yPosition = margin;

      // Header
      doc.setFontSize(24);
      doc.setTextColor(33, 150, 243);
      doc.text("Privacy Policy", pageWidth / 2, yPosition, { align: "center" });
      yPosition += 10;

      // Meta info
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Last updated: January 1, {new Date().getFullYear()} | Data Protection Act Compliant`,
        pageWidth / 2,
        yPosition,
        { align: "center" },
      );
      yPosition += 10;

      doc.setDrawColor(33, 150, 243);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      // Content sections
      const sections = [
        {
          content:
            "Hareeng (Pty) Ltd is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Services. This policy applies to all users including riders, drivers, restaurant partners, and website visitors. We comply with the Data Protection Act and other applicable African privacy laws.",
          title: "1. Overview",
        },
        {
          content:
            "We collect information you provide (name, email, phone, payment details, identity documents) and information collected automatically (location data, device information, usage data, transaction data). We may also receive information from third parties including social media platforms, payment processors, background check providers, and business partners.",
          title: "2. Data We Collect",
        },
        {
          content:
            "We use your data for service delivery, platform improvement, communication, and legal compliance. This includes connecting you with drivers/restaurants, processing payments, providing support, analyzing usage patterns, developing features, sending updates, and meeting regulatory requirements.",
          title: "3. How We Use Your Data",
        },
        {
          content:
            "We do not sell your personal information. We share information with service providers (drivers, delivery partners, restaurants), business partners for analytics, and when required by law. In case of business transfers, your information may be transferred to the new entity.",
          title: "4. Data Sharing",
        },
        {
          content:
            "We implement robust security measures including end-to-end encryption, secure data transmission (TLS/SSL), regular security audits, access controls, employee background checks, data minimization practices, and incident response procedures.",
          title: "5. Data Security",
        },
        {
          content:
            "Under applicable laws, you have rights to access and export your data, correct inaccurate information, request deletion of your data (subject to legal requirements), and opt-out of marketing communications and certain data processing activities.",
          title: "6. Your Privacy Rights",
        },
        {
          content:
            "We use essential cookies for functionality, analytics cookies to understand usage, marketing cookies for relevant advertisements, and preference cookies to remember your settings. You can manage cookie preferences through browser settings or our Cookie Preference Center.",
          title: "7. Cookies & Tracking Technologies",
        },
        {
          content:
            "Our Services are not intended for children under 18. We do not knowingly collect personal information from children. Minors may use our Services only with appropriate parental supervision and consent.",
          title: "8. Childrens Privacy",
        },
        {
          content:
            "We may update this Privacy Policy to reflect changes in practices or legal requirements. We will notify you of material changes through the app, email, or website notice. Continued use of our Services constitutes acceptance of revised terms.",
          title: "9. Policy Updates",
        },
        {
          content:
            "Data Protection Officer, Hareeng (Pty) Ltd, Maseru District, Lesotho, 100. Email: legal@hareeng.org, Phone: +266 50480814. Contact us for privacy-related questions or to exercise your rights.",
          title: "10. Contact Us",
        },
      ];

      sections.forEach((section) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 20) {
          doc.addPage();
          yPosition = margin;
        }

        // Section title
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont("bold");
        doc.text(section.title, margin, yPosition);
        yPosition += 8;

        // Section content
        doc.setFont("normal");
        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
        const splitText = doc.splitTextToSize(section.content, maxWidth);
        doc.text(splitText, margin, yPosition);
        yPosition += splitText.length * 5 + 5;
      });

      // Footer
      const pageCount = (doc as any).internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `© 2026 Hareeng - Transforming Mobility Across Africa`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" },
        );
        doc.text(
          `Page ${i} of ${pageCount}`,
          pageWidth - margin,
          pageHeight - 10,
          { align: "right" },
        );
      }

      // Save PDF
      doc.save("Hareeng-Privacy-Policy.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setDownloadingPDF(false);
    }
  };

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200 pt-16">
        <Breadcrumbs />

        {/* Header */}
        <section className="bg-base-100 py-12">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-success/10 px-4 py-2">
                <Shield className="text-success" size={20} />
                <span className="font-medium text-success">Privacy Policy</span>
              </div>
              <h1 className="mb-4 text-4xl font-bold">Privacy Policy</h1>
              <p className="text-xl text-base-content/70">
                Your privacy matters to us. Learn how we collect, use, and
                protect your data.
              </p>
              <div className="mt-6 flex items-center justify-center gap-4 text-sm text-base-content/70">
                <div className="flex items-center gap-1">
                  <Calendar size={16} />
                  <span>Last updated: January 1, {new Date().getFullYear()}</span>
                </div>
                <span>•</span>
                <span>Data Protection Act Compliant</span>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Overview */}
        <section className="bg-primary/5 py-8">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="mb-4 text-lg font-semibold">Privacy at a Glance</h2>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-center gap-3">
                <Lock className="text-primary" size={20} />
                <span className="text-sm">
                  Your data is encrypted and secure
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Eye className="text-secondary" size={20} />
                <span className="text-sm">
                  You control what data we collect
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Share2 className="text-accent" size={20} />
                <span className="text-sm">
                  We never sell your personal data
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <section className="bg-base-300 py-8">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="mb-4 text-lg font-semibold">Table of Contents</h2>
            <div className="grid gap-2 md:grid-cols-3">
              <a className="link-hover link" href="#overview">
                1. Overview
              </a>
              <a className="link-hover link" href="#data-collection">
                2. Data We Collect
              </a>
              <a className="link-hover link" href="#data-use">
                3. How We Use Data
              </a>
              <a className="link-hover link" href="#data-sharing">
                4. Data Sharing
              </a>
              <a className="link-hover link" href="#data-security">
                5. Data Security
              </a>
              <a className="link-hover link" href="#your-rights">
                6. Your Rights
              </a>
              <a className="link-hover link" href="#cookies">
                7. Cookies & Tracking
              </a>
              <a className="link-hover link" href="#children">
                8. Childrens Privacy
              </a>
              <a className="link-hover link" href="#updates">
                9. Policy Updates
              </a>
              <a className="link-hover link" href="#contact">
                10. Contact Us
              </a>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="prose prose-lg mx-auto max-w-4xl max-w-none px-4">
            <div className="mb-12" id="overview">
              <h2 className="mb-4 text-2xl font-bold">1. Overview</h2>
              <p className="mb-4">
                Hareeng (Pty) Ltd (Hareeng, we, us, or our) is committed to
                protecting your privacy and personal information. This Privacy
                Policy explains how we collect, use, disclose, and safeguard
                your information when you use our Services.
              </p>
              <p className="mb-4">
                This policy applies to all users of our platform, including
                riders, drivers, restaurant partners, and website visitors. By
                using our Services, you consent to the practices described in
                this Privacy Policy.
              </p>
              <div className="alert alert-info">
                <AlertCircle size={20} />
                <span>
                  We comply with the Data Protection Act and other applicable
                  African privacy laws.
                </span>
              </div>
            </div>

            <div className="mb-12" id="data-collection">
              <h2 className="mb-4 text-2xl font-bold">2. Data We Collect</h2>

              <h3 className="mb-3 text-xl font-semibold">
                2.1 Information You Provide
              </h3>
              <ul className="mb-4 list-disc pl-6">
                <li>
                  <strong>Account Information:</strong> Name, email, phone
                  number, profile photo
                </li>
                <li>
                  <strong>Payment Information:</strong> Credit card details, Mobile wallets,
                  billing address
                </li>
                <li>
                  <strong>Identity Verification:</strong> Drivers license, ID
                  documents (for drivers), government-issued ID (for riders), proof of address, utility bills, and other relevant documents as required.
                </li>
                <li>
                  <strong>Communications:</strong> Messages, reviews, support
                  requests
                </li>
              </ul>

              <h3 className="mb-3 text-xl font-semibold">
                2.2 Information We Collect Automatically
              </h3>
              <ul className="mb-4 list-disc pl-6">
                <li>
                  <strong>Location Data:</strong> GPS coordinates, trip routes,
                  pickup/dropoff locations
                </li>
                <li>
                  <strong>Device Information:</strong> Device type, operating
                  system, app version
                </li>
                <li>
                  <strong>Usage Data:</strong> App interactions, features used,
                  time spent
                </li>
                <li>
                  <strong>Transaction Data:</strong> Trip details, payment
                  history, earnings
                </li>
              </ul>

              <h3 className="mb-3 text-xl font-semibold">
                2.3 Information from Third Parties
              </h3>
              <ul className="mb-4 list-disc pl-6">
                <li>Social media platforms (if you connect your accounts)</li>
                <li>Payment processors and financial institutions</li>
                <li>Background check providers (for drivers)</li>
                <li>Business partners and affiliates</li>
              </ul>
            </div>

            <div className="mb-12" id="data-use">
              <h2 className="mb-4 text-2xl font-bold">
                3. How We Use Your Data
              </h2>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body">
                    <div className="mb-3 flex items-center gap-3">
                      <Users className="text-primary" size={24} />
                      <h3 className="card-title">Service Delivery</h3>
                    </div>
                    <ul className="space-y-1 text-sm">
                      <li>• Connecting you with drivers/businesses</li>
                      <li>• Processing payments and transactions</li>
                      <li>• Providing customer support</li>
                      <li>• Ensuring safety and security</li>
                    </ul>
                  </div>
                </div>

                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body">
                    <div className="mb-3 flex items-center gap-3">
                      <Settings className="text-secondary" size={24} />
                      <h3 className="card-title">Platform Improvement</h3>
                    </div>
                    <ul className="space-y-1 text-sm">
                      <li>• Analyzing usage patterns</li>
                      <li>• Developing new features</li>
                      <li>• Optimizing routes and pricing</li>
                      <li>• Preventing fraud and abuse</li>
                    </ul>
                  </div>
                </div>

                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body">
                    <div className="mb-3 flex items-center gap-3">
                      <Share2 className="text-accent" size={24} />
                      <h3 className="card-title">Communication</h3>
                    </div>
                    <ul className="space-y-1 text-sm">
                      <li>• Sending service updates</li>
                      <li>• Marketing communications (with consent)</li>
                      <li>• Safety notifications</li>
                      <li>• Legal and regulatory notices</li>
                    </ul>
                  </div>
                </div>

                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body">
                    <div className="mb-3 flex items-center gap-3">
                      <Shield className="text-success" size={24} />
                      <h3 className="card-title">Legal Compliance</h3>
                    </div>
                    <ul className="space-y-1 text-sm">
                      <li>• Meeting regulatory requirements</li>
                      <li>• Responding to legal requests</li>
                      <li>• Protecting our rights and property</li>
                      <li>• Ensuring public safety</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12" id="data-sharing">
              <h2 className="mb-4 text-2xl font-bold">4. Data Sharing</h2>
              <p className="mb-4">
                We do not sell your personal information to third parties. We
                may share your information in the following circumstances:
              </p>

              <h3 className="mb-3 text-xl font-semibold">
                4.1 Service Providers
              </h3>
              <p className="mb-4">
                We share information with drivers, delivery partners, and
                businesses to facilitate services. This includes names, phone
                numbers, and location data.
              </p>

              <h3 className="mb-3 text-xl font-semibold">
                4.2 Business Partners
              </h3>
              <p className="mb-4">
                We may share aggregated, non-personal information with partners
                for business purposes such as analytics and market research.
              </p>

              <h3 className="mb-3 text-xl font-semibold">
                4.3 Legal Requirements
              </h3>
              <p className="mb-4">
                We may disclose information when required by law, court order,
                or government request, or to protect safety and security.
              </p>

              <h3 className="mb-3 text-xl font-semibold">
                4.4 Business Transfers
              </h3>
              <p>
                In the event of a merger, acquisition, or sale of assets, your
                information may be transferred to the new entity.
              </p>
            </div>

            <div className="mb-12" id="data-security">
              <h2 className="mb-4 text-2xl font-bold">5. Data Security</h2>
              <p className="mb-4">
                We implement robust security measures to protect your personal
                information:
              </p>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="card border border-success/20 bg-success/5">
                  <div className="card-body">
                    <h3 className="mb-2 font-semibold">Technical Safeguards</h3>
                    <ul className="space-y-1 text-sm">
                      <li>• End-to-end encryption</li>
                      <li>• Secure data transmission (TLS/SSL)</li>
                      <li>• Regular security audits</li>
                      <li>• Access controls and monitoring</li>
                    </ul>
                  </div>
                </div>

                <div className="card border border-info/20 bg-info/5">
                  <div className="card-body">
                    <h3 className="mb-2 font-semibold">
                      Operational Safeguards
                    </h3>
                    <ul className="space-y-1 text-sm">
                      <li>• Employee background checks</li>
                      <li>• Data minimization practices</li>
                      <li>• Incident response procedures</li>
                      <li>• Regular staff training</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12" id="your-rights">
              <h2 className="mb-4 text-2xl font-bold">
                6. Your Privacy Rights
              </h2>
              <p className="mb-4">
                Under the Data Protection Act and other applicable laws, you
                have the following rights:
              </p>

              <div className="space-y-4">
                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body">
                    <h3 className="card-title text-lg">Access & Portability</h3>
                    <p className="text-sm">
                      Request a copy of your personal information and export
                      your data in a machine-readable format.
                    </p>
                  </div>
                </div>

                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body">
                    <h3 className="card-title text-lg">Correction & Updates</h3>
                    <p className="text-sm">
                      Update or correct inaccurate personal information in your
                      account settings or by contacting us.
                    </p>
                  </div>
                </div>

                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body">
                    <h3 className="card-title text-lg">Deletion</h3>
                    <p className="text-sm">
                      Request deletion of your personal information, subject to
                      legal and business requirements.
                    </p>
                  </div>
                </div>

                <div className="card bg-base-100 shadow-lg">
                  <div className="card-body">
                    <h3 className="card-title text-lg">Opt-out</h3>
                    <p className="text-sm">
                      Unsubscribe from marketing communications and opt-out of
                      certain data processing activities.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12" id="cookies">
              <h2 className="mb-4 text-2xl font-bold">
                7. Cookies & Tracking Technologies
              </h2>
              <p className="mb-4">
                We use cookies and similar technologies to enhance your
                experience:
              </p>
              <ul className="mb-4 list-disc pl-6">
                <li>
                  <strong>Essential Cookies:</strong> Required for basic
                  platform functionality
                </li>
                <li>
                  <strong>Analytics Cookies:</strong> Help us understand how you
                  use our Services
                </li>
                <li>
                  <strong>Marketing Cookies:</strong> Used to show relevant
                  advertisements
                </li>
                <li>
                  <strong>Preference Cookies:</strong> Remember your settings
                  and preferences
                </li>
              </ul>
              <p>
                You can manage cookie preferences through your browser settings
                or our Cookie Preference Center.
              </p>
            </div>

            <div className="mb-12" id="children">
              <h2 className="mb-4 text-2xl font-bold">8. Childrens Privacy</h2>
              <p className="mb-4">
                Our Services are not intended for children under 18. We do not
                knowingly collect personal information from children. If you
                believe we have collected information from a child, please
                contact us immediately.
              </p>
              <p>
                Minors may use our Services only with appropriate parental
                supervision and consent.
              </p>
            </div>

            <div className="mb-12" id="updates">
              <h2 className="mb-4 text-2xl font-bold">9. Policy Updates</h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time to reflect
                changes in our practices or legal requirements. We will notify
                you of material changes through the app, email, or website
                notice.
              </p>
              <p>
                Continued use of our Services after policy updates constitutes
                acceptance of the revised terms.
              </p>
            </div>

            <div className="mb-12" id="contact">
              <h2 className="mb-4 text-2xl font-bold">10. Contact Us</h2>
              <p className="mb-4">
                For privacy-related questions or to exercise your rights,
                contact our Data Protection Officer:
              </p>
              <div className="card bg-base-100 shadow-lg">
                <div className="card-body">
                  <h3 className="card-title">Privacy Contact</h3>
                  <div className="space-y-2">
                    <p>Data Protection Officer</p>
                    <p>Hareeng (Pty) Ltd</p>
                    <p>Maseru District, Lesotho, 100</p>
                    <div className="flex items-center gap-2">
                      <Mail size={16} />
                      <span>legal@hareeng.org</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} />
                      <span>+266 50480814</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Actions */}
        <section className="bg-base-100 py-8">
          <div className="mx-auto max-w-4xl px-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-base-content/70">
                <p>
                  This policy complies with African privacy laws including the
                  Data Protection Act.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handlePrintPolicy}
                  title="Print Privacy Policy"
                >
                  <Printer size={16} />
                  Print this Policy
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={downloadingPDF}
                  onClick={handleDownloadPDF}
                  title="Download Privacy Policy as PDF"
                >
                  {downloadingPDF ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Download PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
